/**
 * Edit Fee Modal
 *
 * Modal for editing an existing fee's pricing configuration (fixed, tiered, or percentage).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Trash2, Loader2, DollarSign, Layers, Percent, Save } from 'lucide-react';
import { toast } from 'sonner';
import { PricingType, GroupedFeeTiers } from '@/types/title-escrow';
import { titleEscrowService } from '@/services/titleEscrowService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('EditFeeModal', 'title-escrow');

// Format number with commas (1000000 -> "1,000,000")
const formatWithCommas = (value: string): string => {
  const num = value.replace(/,/g, '');
  if (!num || isNaN(Number(num))) {
return value;
}
  // Handle decimals properly
  const parts = num.split('.');
  parts[0] = Number(parts[0]).toLocaleString('en-US');
  return parts.join('.');
};

// Parse formatted string to number string (remove commas)
const parseCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

interface TierInput {
  id: string;
  minLoanAmount: string;
  maxLoanAmount: string;
  amount: string;
}

interface EditFeeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  fee: GroupedFeeTiers | null;
  onFeeUpdated: () => void;
}

export const EditFeeModal: React.FC<EditFeeModalProps> = ({
  open,
  onOpenChange,
  companyId,
  fee,
  onFeeUpdated
}) => {
  // Pricing configuration
  const [pricingType, setPricingType] = useState<PricingType>('fixed');
  const [fixedAmount, setFixedAmount] = useState('');
  const [tiers, setTiers] = useState<TierInput[]>([
    { id: '1', minLoanAmount: '0', maxLoanAmount: '500000', amount: '' }
  ]);
  const [percentageRate, setPercentageRate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Conditions
  const [isConditional, setIsConditional] = useState(false);
  const [conditionDescription, setConditionDescription] = useState('');

  // Saving
  const [saving, setSaving] = useState(false);

  // Initialize form with existing fee data
  useEffect(() => {
    if (open && fee) {
      setPricingType(fee.pricingType);
      setIsConditional(fee.isConditional);
      setConditionDescription(fee.conditionDescription || '');

      if (fee.pricingType === 'fixed' && fee.tiers[0]) {
        setFixedAmount(String(fee.tiers[0].amount || ''));
      } else if (fee.pricingType === 'tiered') {
        setTiers(
          fee.tiers.map((tier) => ({
            id: tier.id,
            minLoanAmount: String(tier.minLoanAmount || 0),
            maxLoanAmount: tier.maxLoanAmount !== null ? String(tier.maxLoanAmount) : '',
            amount: String(tier.amount || '')
          }))
        );
      } else if (fee.pricingType === 'percentage' && fee.tiers[0]) {
        setPercentageRate(String((fee.tiers[0].percentageRate || 0) * 100));
        // Note: min/max amounts for percentage would need to be loaded from the full schedule data
      }
    }
  }, [open, fee]);

  const resetForm = () => {
    setPricingType('fixed');
    setFixedAmount('');
    setTiers([{ id: '1', minLoanAmount: '0', maxLoanAmount: '500000', amount: '' }]);
    setPercentageRate('');
    setMinAmount('');
    setMaxAmount('');
    setIsConditional(false);
    setConditionDescription('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const lastMax = lastTier ? parseFloat(parseCommas(lastTier.maxLoanAmount)) : 0;
    const newMinAmount = !isNaN(lastMax) && lastMax > 0 ? String(lastMax + 1) : '0';
    setTiers([
      ...tiers,
      {
        id: String(Date.now()),
        minLoanAmount: newMinAmount,
        maxLoanAmount: '',
        amount: ''
      }
    ]);
  };

  const removeTier = (id: string) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter((t) => t.id !== id));
    }
  };

  const updateTier = (id: string, field: keyof TierInput, value: string) => {
    setTiers((prev) => {
      const updated = prev.map((t) => (t.id === id ? { ...t, [field]: value } : t));

      // If maxLoanAmount changed, auto-cascade to update next tier's minLoanAmount
      if (field === 'maxLoanAmount') {
        const idx = updated.findIndex((t) => t.id === id);
        if (idx >= 0 && idx < updated.length - 1 && value) {
          const parsedMax = parseFloat(parseCommas(value));
          if (!isNaN(parsedMax)) {
            const nextMin = String(parsedMax + 1);
            updated[idx + 1] = { ...updated[idx + 1], minLoanAmount: nextMin };
          }
        }
      }

      return updated;
    });
  };

  const handleSave = async () => {
    if (!fee) {
return;
}

    try {
      setSaving(true);

      // First, remove all existing tiers for this fee
      await titleEscrowService.removeAllFeeTiers(companyId, fee.feeId);

      // Then add new pricing based on type
      if (pricingType === 'fixed') {
        const amount = parseFloat(fixedAmount);
        if (isNaN(amount) || amount < 0) {
          toast.error('Please enter a valid amount');
          setSaving(false);
          return;
        }

        await titleEscrowService.addFeeToSchedule({
          companyId,
          feeId: fee.feeId,
          pricingType: 'fixed',
          amount,
          isConditional,
          conditionDescription: isConditional ? conditionDescription : undefined
        });
      } else if (pricingType === 'tiered') {
        const validTiers = tiers
          .filter((t) => t.amount)
          .map((t) => ({
            minLoanAmount: parseFloat(parseCommas(t.minLoanAmount)) || 0,
            maxLoanAmount: t.maxLoanAmount ? parseFloat(parseCommas(t.maxLoanAmount)) : null,
            amount: parseFloat(parseCommas(t.amount))
          }));

        if (validTiers.length === 0) {
          toast.error('Please enter at least one tier');
          setSaving(false);
          return;
        }

        await titleEscrowService.addTieredFee(companyId, fee.feeId, validTiers, {
          isConditional,
          conditionDescription: isConditional ? conditionDescription : undefined
        });
      } else if (pricingType === 'percentage') {
        const rate = parseFloat(percentageRate);
        if (isNaN(rate) || rate < 0) {
          toast.error('Please enter a valid percentage');
          setSaving(false);
          return;
        }

        await titleEscrowService.addFeeToSchedule({
          companyId,
          feeId: fee.feeId,
          pricingType: 'percentage',
          percentageRate: rate / 100, // Convert from percentage to decimal
          minAmount: minAmount ? parseFloat(minAmount) : undefined,
          maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
          isConditional,
          conditionDescription: isConditional ? conditionDescription : undefined
        });
      }

      toast.success('Fee updated successfully');
      onFeeUpdated();
      handleClose();
    } catch (error) {
      log.error('Failed to update fee', {}, error as Error);
      toast.error('Failed to update fee');
    } finally {
      setSaving(false);
    }
  };

  if (!fee) {
return null;
}

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Fee</DialogTitle>
          <DialogDescription>Update pricing configuration for this fee.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <div className="space-y-6">
            {/* Selected Fee Display */}
            <div className="p-3 bg-muted rounded-md">
              <p className="font-medium">{fee.feeName}</p>
              <p className="text-sm text-muted-foreground">ID: {fee.encompassFeeId}</p>
            </div>

            {/* Pricing Type Selection */}
            <div className="space-y-3">
              <Label>Pricing Type</Label>
              <RadioGroup
                value={pricingType}
                onValueChange={(v) => setPricingType(v as PricingType)}
                className="grid grid-cols-3 gap-4"
              >
                <div>
                  <RadioGroupItem value="fixed" id="edit-fixed" className="peer sr-only" />
                  <Label
                    htmlFor="edit-fixed"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                  >
                    <DollarSign className="mb-2 h-6 w-6" />
                    <span className="text-sm font-medium">Fixed Amount</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="tiered" id="edit-tiered" className="peer sr-only" />
                  <Label
                    htmlFor="edit-tiered"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                  >
                    <Layers className="mb-2 h-6 w-6" />
                    <span className="text-sm font-medium">Tiered</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="percentage" id="edit-percentage" className="peer sr-only" />
                  <Label
                    htmlFor="edit-percentage"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                  >
                    <Percent className="mb-2 h-6 w-6" />
                    <span className="text-sm font-medium">Percentage</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Fixed Amount Input */}
            {pricingType === 'fixed' && (
              <div className="space-y-2">
                <Label htmlFor="editFixedAmount">Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="editFixedAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={fixedAmount}
                    onChange={(e) => setFixedAmount(e.target.value)}
                    className="pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}

            {/* Tiered Pricing Input */}
            {pricingType === 'tiered' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Loan Amount Tiers</Label>
                  <Button variant="outline" size="sm" onClick={addTier}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Tier
                  </Button>
                </div>
                <div className="max-h-[200px] overflow-y-auto pr-1">
                  <div className="space-y-2">
                    {tiers.map((tier) => (
                      <div key={tier.id} className="flex items-center gap-2">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                              $
                            </span>
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={formatWithCommas(tier.minLoanAmount)}
                              onChange={(e) =>
                                updateTier(tier.id, 'minLoanAmount', parseCommas(e.target.value))
                              }
                              className="pl-5 text-sm"
                              placeholder="Min"
                            />
                          </div>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                              $
                            </span>
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={formatWithCommas(tier.maxLoanAmount)}
                              onChange={(e) =>
                                updateTier(tier.id, 'maxLoanAmount', parseCommas(e.target.value))
                              }
                              className="pl-5 text-sm"
                              placeholder="Max (no limit)"
                            />
                          </div>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                              $
                            </span>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={formatWithCommas(tier.amount)}
                              onChange={(e) =>
                                updateTier(tier.id, 'amount', parseCommas(e.target.value))
                              }
                              className="pl-5 text-sm"
                              placeholder="Amount"
                            />
                          </div>
                        </div>
                        {tiers.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeTier(tier.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Percentage Input */}
            {pricingType === 'percentage' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="editPercentageRate">Percentage Rate</Label>
                  <div className="relative w-32">
                    <Input
                      id="editPercentageRate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={percentageRate}
                      onChange={(e) => setPercentageRate(e.target.value)}
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                      %
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="editMinAmount">Minimum Fee</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        id="editMinAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                        className="pl-7"
                        placeholder="No minimum"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editMaxAmount">Maximum Fee (Cap)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        id="editMaxAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        className="pl-7"
                        placeholder="No maximum"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Conditional Fee */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="editIsConditional"
                  checked={isConditional}
                  onCheckedChange={(checked) => setIsConditional(!!checked)}
                />
                <Label htmlFor="editIsConditional" className="cursor-pointer">
                  This fee is conditional (only charged when applicable)
                </Label>
              </div>
              {isConditional && (
                <Input
                  placeholder="e.g., 'if credit card payment', 'per deed needed'"
                  value={conditionDescription}
                  onChange={(e) => setConditionDescription(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditFeeModal;
